/**
 * Tonnie Hero Colors - Shared Configuration
 *
 * This file defines the standard color mapping for all Tonnie heroes. The web
 * portals read `HERO_COLORS` (CSS values); the phone's `useColors()` hero keys
 * are the same tones as hex (`TONE_HEX`), so every portal paints a hero alike.
 *
 * Reference: Tonnie Brand Guide v2.5 - Section 6 (Heroes)
 */

import { toneVars, type Tone } from './tones';

export type HeroType = 'checkup' | 'teleportal' | 'qultureup' | 'care' | 'passport';

/**
 * The colour tone each hero wears (globals.css `--tone-<t>-*`, contrast-tested in
 * tones.spec.ts). Passport has no tone: it wears the Tonnie brand (`--primary`).
 *
 * - CheckUp: blue (assessment, measurement)
 * - TelePortal: green (mobility, travel, progress)
 * - QultureUp: violet (learning, culture, growth)
 * - Care: rose (support, wellbeing, heart)
 */
export const HERO_TONE: Record<Exclude<HeroType, 'passport'>, Tone> = {
  checkup: 'blue',
  teleportal: 'green',
  qultureup: 'violet',
  care: 'rose',
};

/**
 * A hero's colours on the web, as CSS values that follow the theme (light and
 * dark). Use them where a style takes a colour value — an inline style, an SVG
 * `fill` / `stroke`, a Recharts series. Where only a class is needed, prefer the
 * matching Tailwind class (`HERO_CLASSES` in @tonnie/ui).
 *
 * They are `var()` expressions, so never append a hex alpha to one
 * (`${primary}20` is invalid CSS): use `light` / `border`, or
 * `color-mix(in oklab, <value> N%, transparent)`. The phone has no CSS and reads
 * its hero colours from `useColors()`.
 */
export interface HeroColorConfig {
  /** The hero's fill (a button, a bar, a dot, a chart series). */
  primary: string;
  /** Text or an icon ON the `primary` fill. */
  onPrimary: string;
  /** Text or an icon in the hero colour — reads on the page, a card and `light`. */
  text: string;
  /** The hero's tint (an icon box, a chip, a banner). */
  light: string;
  /** A border on the tint. */
  border: string;
}

function toneColors(tone: Tone): HeroColorConfig {
  const v = toneVars(tone);
  return { primary: v.solid, onPrimary: v.onSolid, text: v.text, light: v.subtle, border: v.line };
}

/** Every hero's colours — one table for journey-web, business-web and the shared UI. */
export const HERO_COLORS: Record<HeroType, HeroColorConfig> = {
  checkup: toneColors(HERO_TONE.checkup),
  teleportal: toneColors(HERO_TONE.teleportal),
  qultureup: toneColors(HERO_TONE.qultureup),
  care: toneColors(HERO_TONE.care),
  passport: {
    primary: 'var(--primary)',
    onPrimary: 'var(--primary-foreground)',
    text: 'var(--primary-accessible)',
    light: 'color-mix(in oklab, var(--primary) 10%, transparent)',
    border: 'color-mix(in oklab, var(--primary) 20%, transparent)',
  },
};

/**
 * Get hero color configuration
 */
export function getHeroColors(hero: HeroType): HeroColorConfig {
  return HERO_COLORS[hero];
}

/**
 * Hero icons mapping (Lucide icon names)
 *
 * Standard icons for all heroes - must be consistent across all portals:
 * - CheckUp: ClipboardCheck (assessment, measurement)
 * - TelePortal: Plane (mobility, travel)
 * - QultureUp: GraduationCap (learning, education)
 * - Care: Heart (wellbeing, support)
 * - Passport: BadgeCheck (verified achievement, journey record)
 */
export const HERO_ICONS: Record<HeroType, string> = {
  checkup: 'ClipboardCheck',
  teleportal: 'Plane',
  qultureup: 'GraduationCap',
  care: 'Heart',
  passport: 'BadgeCheck',
};

/**
 * Hero display names
 */
export const HERO_NAMES: Record<HeroType, string> = {
  checkup: 'CheckUp',
  teleportal: 'TelePortal',
  qultureup: 'QultureUp',
  care: 'Care',
  passport: 'Passport',
};

/**
 * Hero subtitles for cards
 */
export const HERO_SUBTITLES: Record<HeroType, string> = {
  checkup: 'Measure readiness',
  teleportal: 'Plan & execute move',
  qultureup: 'Learn & adapt',
  care: 'Support & wellbeing',
  passport: 'Journey record',
};

/**
 * Business portal hero names (alternative names for B2B context)
 */
export const HERO_BUSINESS_NAMES: Record<HeroType, string> = {
  checkup: 'Vetting',
  teleportal: 'Mobility',
  qultureup: 'Adaptation',
  care: 'Care',
  passport: 'Evidence',
};
